import React, { useEffect } from 'react';
import { autoId, emailRegex, formFieldErrorPropsV2 } from '@workday/upc-common';
import { FormField } from '@workday/canvas-kit-react/form-field';
import { TextInput } from '@workday/canvas-kit-react/text-input';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { useForm } from 'react-hook-form';
import styles from './alias-email-entry.module.scss';
import { ID_ALIAS_TEXT_INPUT, ID_CANCEL_BUTTON, ID_SUBMIT_BUTTON } from '../../../../autoIds';
import { useAddAliasMutation, UserAlias } from '../../../../slices/account-api.slice';
import { LoadingDots } from '@workday/canvas-kit-react';
import { useTranslation } from 'react-i18next';
import { useErrorHandling } from '../../../../hooks/useErrorHandling';
import { serverError } from '../../../../utils/server-error';
import { OverflowTooltip } from '@workday/canvas-kit-react/tooltip';

export const hasExistingAlias = (aliases, alias) =>
    aliases.some(existingAlias => existingAlias.alias.toLowerCase() === alias.toLowerCase());

interface AliasEmailEntryProps {
    alias: string;
    onClose: () => void;
    setAlias: (alias: string) => void;
    aliases: Array<UserAlias>;
}

interface AliasEmailFormState {
    alias: string;
}

export function AliasEmailEntry({ alias, onClose, setAlias, aliases }: AliasEmailEntryProps) {
    const { formState, handleSubmit, register, getValues, setError } = useForm<AliasEmailFormState>({
        reValidateMode: 'onSubmit',
        defaultValues: {
            alias
        }
    });

    const { t } = useTranslation('account');

    const [addAlias, addAliasMutation] = useAddAliasMutation();

    useEffect(() => {
        if (addAliasMutation.isSuccess) {
            setAlias(getValues('alias'));
        }
    }, [addAliasMutation.isSuccess, getValues, setAlias]);

    useErrorHandling(addAliasMutation.error, {
        inlineErrors: ['UPC-1017', 'UPC-1029'],
        onInlineError: error => setError('alias', serverError(error))
    });

    const onSubmit = ({ alias }) => {
        addAlias({ alias, type: 'EMAIL' });
    };

    return addAliasMutation.isLoading ? (
        <LoadingDots className={styles.loading} />
    ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
            <h1>{t('AliasEmailEntry.title')}</h1>
            <h2>{t('AliasEmailEntry.description')}</h2>

            <div className={styles.body}>
                <FormField
                    label={t('AliasEmailEntry.EmailFieldTitle')}
                    id='alias'
                    grow={true}
                    {...formFieldErrorPropsV2(
                        formState.errors.alias,
                        undefined,
                        t('Required.fieldErrorLabel', { ns: 'shared' })
                    )}>
                    <TextInput
                        {...autoId(ID_ALIAS_TEXT_INPUT)}
                        autoFocus={true}
                        {...register('alias', {
                            required: t('Required.fieldGeneric', { ns: 'shared' }),
                            pattern: {
                                value: emailRegex,
                                message: t('AliasEmailEntry.emailValidationError')
                            },
                            validate: {
                                duplicateAlias: v =>
                                    hasExistingAlias(aliases, v) ? t('AliasEmailEntry.duplicateAliasError') : true
                            }
                        })}
                    />
                </FormField>
            </div>

            <div className='settingsModalContent__footer'>
                <OverflowTooltip>
                    <PrimaryButton
                        type='submit'
                        {...autoId(ID_SUBMIT_BUTTON)}
                        className='settingsModalContent__submit'
                        size='large'
                        grow={true}>
                        {t('AliasEmailEntry.submitButton')}
                    </PrimaryButton>
                </OverflowTooltip>
                <div className='settingsModalContent__cancel'>
                    <TertiaryButton {...autoId(ID_CANCEL_BUTTON)} type='button' size='large' onClick={onClose}>
                        {t('Common.cancel', { ns: 'shared' })}
                    </TertiaryButton>
                </div>
            </div>
        </form>
    );
}
